import React, { useRef } from 'react';

import { getCookie } from '@api/HelperAPI';
import { useGetProductOffers } from '@api/lendingAPI/lapiHooks';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconUpload } from '@iwoca/icons';
import { PostDocumentUploadResponse } from '@iwoca/lapi-client/edge';
import { Button } from '@iwoca/orion';
import { v4 } from 'uuid';

import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import { TUploadRejectReasonCode } from '../NewDocUploads.types';

type TUploadButton = {
    documentType?: PostDocumentUploadResponse['data']['document_type'];
    onFileLoading: (id: string, name: string) => void;
    onFileSuccess: (id: string) => void;
    onFileError: (id: string, errorReason: string) => void;
    onFileReject: (id: string, rejectReason: TUploadRejectReasonCode) => void;
    disabled?: boolean;
};

const UploadButton = ({
    documentType = 'other',
    onFileLoading,
    onFileSuccess,
    onFileError,
    onFileReject,
    disabled
}: TUploadButton) => {
    const inputFileRef = useRef<HTMLInputElement>(null);
    const stateKey = useStateKey();

    const { isFetching: isFetchingProductOffers } =
        useGetProductOffers(stateKey);

    const onButtonClick = () => {
        inputFileRef!.current!.click();
    };

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        // e.persist is required due to event pooling (https://legacy.reactjs.org/docs/legacy-event-pooling.html). Need to persist event so target value can be set. Otherwise, get null error.
        // Can be removed when we upgrade to react 17. "As of v17, e.persist() doesn’t do anything because the SyntheticEvent is no longer pooled." - from https://legacy.reactjs.org/docs/events.html
        e.persist();

        if (!e.target.files || !stateKey) {
            return;
        }

        const files = Array.from(e.target.files);

        await Promise.all(
            files.map(async (file) => {
                const uuid = v4();
                onFileLoading(uuid, file.name);
                const body = new FormData();

                body.append('document', file);
                body.append('document_type', documentType);

                const response = await saveUploadedDocuments({
                    stateKey,
                    body
                });

                if (response.errors?.[0]) {
                    onFileError(uuid, response.errors[0].detail);

                    tracking.ampli.newDocUploadsDocumentUploadFailed({
                        'Document name': file.name
                    });

                    return;
                }

                if (response.attributes?.status === 'rejected') {
                    onFileReject(uuid, response.attributes.reject_reason);

                    return;
                }

                onFileSuccess(uuid);

                tracking.ampli.newDocUploadsSuccessfullyUploadedDocument({
                    'Document name': file.name
                });
            })
        );

        // Reset input value to allow same file upload to fire onChange.
        e.target.value = '';
    };

    let buttonText = 'files';
    switch (documentType) {
        case 'bank statement':
            buttonText =
                getCountry() === 'DE' ? 'Kontoauszüge hochladen' : 'statements';
            break;

        case 'business accounts':
        case 'management accounts':
            buttonText = 'accounts';
            break;

        case 'vat return':
            buttonText = 'returns';
            break;

        case 'aged debtor report':
            buttonText = 'report';
            break;

        default:
            break;
    }

    return (
        <>
            <Button
                variant="secondary"
                onClick={onButtonClick}
                type="button"
                disabled={disabled || isFetchingProductOffers}
            >
                {getCountry() === 'DE'
                    ? `${buttonText}`
                    : `Upload ${buttonText}`}
                <IconUpload />
            </Button>
            <input
                multiple
                hidden
                data-testid="upload-input"
                type="file"
                ref={inputFileRef!}
                accept=".jpg, .jpeg, .png, .pdf"
                onChange={handleChange}
            />
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default UploadButton;

async function saveUploadedDocuments({
    stateKey,
    body
}: {
    stateKey: string;
    body: FormData;
}) {
    // eslint-disable-next-line iwoca/prefer-lapi-client
    const response = await fetch(
        `/api/lending/edge/document_upload/${stateKey}/`,
        {
            method: 'POST',
            body: body,
            headers: {
                'x-csrftoken': getCookie('csrftoken') as string,
                enctype: 'multipart/form-data'
            }
        }
    );

    return await response.json();
}
